import { generatePieces } from '../utils/pieceGenerator';
import { resetPuzzleStateInternal } from './puzzleUtils';

// Функция для обработки изображения случайных пазлов (проверка пропорций и обрезание)
async function processRandomPuzzleImage(imageData) {

  console.log("Random puzzle detected, checking aspect ratio...");

  // Создаем временный blob для определения размеров
  const tempBlob = new Blob([imageData], { type: 'image/png' });
  const tempImg = new Image();

  const aspectRatio = await new Promise((resolve, reject) => {
    tempImg.onload = () => {
      const ratio = tempImg.naturalWidth / tempImg.naturalHeight;
      URL.revokeObjectURL(tempImg.src);
      resolve(ratio);
    };
    tempImg.onerror = reject;
    tempImg.src = URL.createObjectURL(tempBlob);
  });

  console.log("Original aspect ratio:", aspectRatio);

  // Проверяем, нужно ли обрезать (пропорции > 3:2 или < 2:3)
  const maxAspectRatio = 3 / 2; // 1.5
  const minAspectRatio = 2 / 3; // 0.666...

  if (aspectRatio > maxAspectRatio) {
    console.log("Image too wide, cropping to 3:2 aspect ratio");
    return await cropImageToAspectRatio(imageData, maxAspectRatio);
  } else if (aspectRatio < minAspectRatio) {
    console.log("Image too tall, cropping to 2:3 aspect ratio");
    return await cropImageToAspectRatio(imageData, minAspectRatio);
  } else {
    console.log("Aspect ratio is acceptable, no cropping needed");
    return imageData;
  }
}

// Функция для обрезания изображения до заданных пропорций
async function cropImageToAspectRatio(imageData, targetAspectRatio) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([imageData], { type: 'image/png' });
    const img = new Image();

    img.onload = () => {
      const { naturalWidth: width, naturalHeight: height } = img;
      const currentAspectRatio = width / height;

      // Вычисляем новые размеры
      let newWidth = width;
      let newHeight = height;
      let offsetX = 0;
      let offsetY = 0;

      if (currentAspectRatio > targetAspectRatio) {
        // Изображение слишком широкое, обрезаем по ширине
        newWidth = Math.round(height * targetAspectRatio);
        offsetX = Math.round((width - newWidth) / 2);
      } else if (currentAspectRatio < targetAspectRatio) {
        // Изображение слишком высокое, обрезаем по высоте
        newHeight = Math.round(width / targetAspectRatio);
        offsetY = Math.round((height - newHeight) / 2);
      } else {
        // Пропорции уже правильные, возвращаем оригинал
        resolve(imageData);
        return;
      }

      // Создаем canvas для обрезки
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');

      // Рисуем обрезанную часть
      ctx.drawImage(img, offsetX, offsetY, newWidth, newHeight, 0, 0, newWidth, newHeight);

      // Конвертируем обратно в ArrayBuffer
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create cropped image blob'));
          return;
        }
        blob.arrayBuffer().then(resolve).catch(reject);
      }, 'image/png');
    };

    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = URL.createObjectURL(blob);
  });
}

// Инициализация пазла
export async function initializePuzzle({ ref, set }) {
  
  const { info, puzzle } = ref;
  const puzzleData = info.game?.puzzle;
  const gameSettings = info.game?.settings;

  // Если нет данных пазла, используем тестовый режим
  if (!puzzleData) {
    console.warn("No puzzle data available, using test mode");
    return createTestPuzzle();
  }
  
  // Если file_id отсутствует, создаем один
  const file_id = puzzleData.file_id || "default_puzzle_id";
  
  if (puzzle.state.isLoading) {
    console.log("Puzzle already loading, skipping initialization");
    return;
  }

  if (puzzle.state.isInitialized && puzzle.state.file_id === puzzleData.file_id) {
     console.log("Puzzle already initialized, syncing from history");
     ref.puzzle.methods.syncPiecesFromHistory();
     return;
  }

  window.croco?.log("Initializing puzzle...");
  set({ spinner: Date.now() + 5000 });
  
  // Сбрасываем состояние перед инициализацией
  if (ref.puzzle.state.bgUrl) URL.revokeObjectURL(ref.puzzle.state.bgUrl);
  if (ref.puzzle.state.highlightTimeoutId) clearTimeout(ref.puzzle.state.highlightTimeoutId);
  ref.puzzle.state = resetPuzzleStateInternal();
  ref.puzzle.state.isLoading = true;
  ref.puzzle.methods.redraw();

  try {
    console.log("Loading puzzle image from server for file_id:", file_id);
    
    // Проверяем доступность work.methods.hostActionPromise
    if (!ref.work?.methods?.hostActionPromise) {
      console.error("hostActionPromise method is not available");
      throw new Error("Host communication method not available");
    }
    
    let response;
    try {
      response = await ref.work.methods.hostActionPromise('loadPuzzle', { file_id });
      console.log("Server responded with image data:", response ? "Success" : "Empty response");
    } catch (err) {
      console.error("Error calling hostActionPromise:", err);
      throw new Error(`Failed to load puzzle data: ${err.message || "unknown error"}`);
    }
    
    let { image } = response || {};
    if (!image || image.byteLength === 0) {
      throw new Error("No image data received from server");
    }
    
    console.log("Image data received, size:", image.byteLength, "bytes");

    // Обрабатываем изображение для случайных пазлов (проверка и обрезание пропорций)
    if (info.game?.isRandom) {
      image = await processRandomPuzzleImage(image);
    }

    const config = { ...ref.puzzle.config, ...gameSettings?.puzzleConfig };
    ref.puzzle.state.config = config; // Обновляем рабочий конфиг
    console.log("Puzzle config applied:", config);

    // --- Canvas Image Loading ---
    // Освобождаем старый URL и элемент, если они есть
    if (ref.puzzle.state.bgUrl) URL.revokeObjectURL(ref.puzzle.state.bgUrl);
    ref.puzzle.state.puzzleImageElement = null;

    const blob = new Blob([image], { type: 'image/png' });
    const blobUrl = URL.createObjectURL(blob);
    ref.puzzle.state.bgUrl = blobUrl; // Сохраняем URL для возможного использования

    // Создаем и загружаем HTMLImageElement для Canvas
    const imgElement = new Image();
    imgElement.onload = () => {
      ref.puzzle.state.puzzleImageElement = imgElement; // Сохраняем загруженный элемент
      window.croco?.log('Puzzle image loaded into Image element for Canvas.');
      ref.puzzle.methods.redraw({ lite: true });
    };
    imgElement.onerror = () => console.error("Failed to load puzzle image into Image element for Canvas.");
    imgElement.src = blobUrl; // Запускаем загрузку
    // --------------------------

    // Обеспечим правильную обработку ошибок generatePieces
    let piecesGenData;
    try {
      piecesGenData = await generatePieces(image, config, ref);
      console.log("Pieces generated successfully");
    } catch (genError) {
      console.error("Error generating pieces:", genError);
      throw new Error("Failed to generate puzzle pieces: " + (genError.message || "unknown error"));
    }
    
    const { pieces, imageDimensions, pieceBaseDimensions, rows, cols } = piecesGenData;

    ref.puzzle.state.isLoading = false;
    ref.puzzle.state.isInitialized = true;
    ref.puzzle.state.puzzleImage = image;
    ref.puzzle.state.file_id = file_id;
    ref.puzzle.state.imageDimensions = imageDimensions;
    ref.puzzle.state.pieceBaseDimensions = pieceBaseDimensions;
    ref.puzzle.state.rows = rows;
    ref.puzzle.state.cols = cols;
    ref.puzzle.state.pieces = pieces;

    // Обновляем Zustand и вызываем updateLayout *после* установки всех данных

    ref.puzzle.methods.redraw();
    ref.puzzle.methods.recalculateLayout();

    setTimeout(() => {
      ref.puzzle.state.finalLayout = Date.now();
      ref.puzzle.methods.recalculateLayout();
      ref.puzzle.methods.redraw();
    }, 0);

    // Синхронизируем историю после обновления состояния и updateLayout
    ref.puzzle.methods.syncPiecesFromHistory();

    window.Telegram.WebApp.onEvent('settingsButtonClicked', () => {
      ref.menu.methods.openMenu('puzzleSettings');
    });


    window.croco?.log('Puzzle Initialized.');

    // Добавляем дополнительную информацию
    console.log(`Puzzle initialized with ${rows}x${cols} (${Object.keys(pieces).length} pieces)`);

  } catch (error) {
    console.error('Failed to initialize puzzle:', error);
    if (ref.puzzle.state.bgUrl) URL.revokeObjectURL(ref.puzzle.state.bgUrl);
    if (ref.puzzle.state.highlightTimeoutId) clearTimeout(ref.puzzle.state.highlightTimeoutId);
    ref.puzzle.state = resetPuzzleStateInternal(); // Сброс при ошибке
    ref.puzzle.state.isLoading = false; // Убедиться, что isLoading false
    set({ gameError: { error: 'Failed to load puzzle image or generate pieces.' } });
  } finally {
    set({ spinner: 0 });
    setTimeout(() => {
      ref.puzzle.methods.redraw();
    }, 10);
  }
}

// Функция для создания тестового пазла
async function createTestPuzzle() {
  // ... код создания тестового пазла ...
}
